import React, { FC, useEffect, useState } from 'react';
import Paper from '@mui/material/Paper';
import Hidden from '@mui/material/Hidden';
import { useTranslation } from 'react-i18next';
import { Alert, Box, CircularProgress, Step, StepLabel, Stepper } from '@mui/material';
import { useParams } from 'react-router-dom';
import { useApolloClient, useLazyQuery } from '@apollo/client';
import { Order } from 'models/order';
import { orderForSubcontractTemplate } from 'apollo/templates/order/query.graphql';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import SubcontractorStep from 'components/order-flow/subcontract-order/subcontractor-step/subcontractor-step.component';
import RestorationsStep from 'components/order-flow/subcontract-order/restorations-step/restorations-step.component';
import ContentStep from 'components/order-flow/subcontract-order/content-step/content-step.component';
import CommunicationStep from 'components/order-flow/subcontract-order/communication-step/communication-step.component';
import ConfirmSubcontractStep from 'components/order-flow/subcontract-order/confirm-step/confirm-subcontract-step.component';
import { SubcontractOrderProvider } from './subcontract-order-context';

/**
 * DD-3484 Marketplace: a lab passes all or part of a received order to a connected
 * partner lab, choosing exactly what content travels. The sub-order exists only on
 * confirmation; leaving this flow at any step creates nothing.
 */
const SubcontractOrderSteps: FC = () => {
    const { t: translate } = useTranslation(['orders']);
    const [activeStep, setActiveStep] = useState(0);

    const steps = [
        translate('orders:SUBCONTRACTOR'),
        translate('orders:CONSTRUCTIONS'),
        translate('orders:SUBCONTRACT_CONTENT'),
        translate('orders:SUBCONTRACT_COMMUNICATION'),
        translate('orders:CONFIRM'),
    ];

    const handleNext = () => setActiveStep((previousStep) => previousStep + 1);
    const handleBack = () => setActiveStep((previousStep) => previousStep - 1);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [activeStep]);

    const handleStepperClick = (step: number) => {
        if (step < activeStep) {
            setActiveStep(step);
        }
    };

    const getStepContent = () => {
        switch (activeStep) {
            case 0:
                return <SubcontractorStep onNextClick={handleNext} />;
            case 1:
                return <RestorationsStep onBackClick={handleBack} onNextClick={handleNext} />;
            case 2:
                return <ContentStep onBackClick={handleBack} onNextClick={handleNext} />;
            case 3:
                return <CommunicationStep onBackClick={handleBack} onNextClick={handleNext} />;
            case 4:
                return <ConfirmSubcontractStep onBackClick={handleBack} />;
            default:
                return 'Unknown stepIndex';
        }
    };

    return (
        <Box sx={{ maxWidth: '1200px' }} display="flex" flexDirection="column" mx="auto">
            <Box mb={2}>
                <Hidden mdDown>
                    <Stepper activeStep={activeStep}>
                        {steps.map((label, index) => (
                            <Step sx={{ cursor: 'pointer' }} key={label} onClick={() => handleStepperClick(index)}>
                                <StepLabel>{label}</StepLabel>
                            </Step>
                        ))}
                    </Stepper>
                </Hidden>
            </Box>
            <Paper elevation={1}>
                <Box p={3}>{getStepContent()}</Box>
            </Paper>
        </Box>
    );
};

const SubcontractOrder: FC = () => {
    const { t: translate } = useTranslation(['orders']);
    const params = useParams<{ parentOrderId: string }>();
    const [parentOrder, setParentOrder] = useState<Order>();
    const { showBoundary } = useDenthubErrorBoundary();
    const apolloClient = useApolloClient();

    const [fetchParentOrder, { loading }] = useLazyQuery(orderForSubcontractTemplate, {
        fetchPolicy: 'no-cache',
        onCompleted: ({ order }: { order: Order }) => setParentOrder(order),
        onError(error) {
            showBoundary(error);
        },
    });

    useEffect(() => {
        if (params.parentOrderId) {
            fetchParentOrder({ variables: { orderId: params.parentOrderId } });
        }
    }, [params.parentOrderId]);

    // Not through the lazy query: its loading state would swap the steps for the spinner
    // and throw away the lab's progress through the flow.
    const refreshParentOrder = async () => {
        const { data } = await apolloClient.query<{ order: Order }>({
            query: orderForSubcontractTemplate,
            variables: { orderId: params.parentOrderId },
            fetchPolicy: 'no-cache',
        });
        setParentOrder(data.order);
        return data.order;
    };

    if (loading || !parentOrder) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
                <CircularProgress size={80} />
            </Box>
        );
    }

    // The backend rejects these outright; showing the reason beats a server error.
    if (parentOrder.isDraft || parentOrder.rejectStatus) {
        return (
            <Box sx={{ maxWidth: '1200px' }} mx="auto">
                <Alert severity="warning">{translate('orders:SUBCONTRACT_PARENT_NOT_ACTIVE')}</Alert>
            </Box>
        );
    }

    return (
        <SubcontractOrderProvider parentOrder={parentOrder} refreshParentOrder={refreshParentOrder}>
            <SubcontractOrderSteps />
        </SubcontractOrderProvider>
    );
};

export default SubcontractOrder;
